import { engineMap } from "@/lib/engine";
import { getCountryRules } from "@/lib/country/registry";
import { CalculatorConfig } from "@/lib/country/types";

/**
 * Executes the appropriate calculation engine based on the calculator configuration
 * and returns the calculated results.
 */
export function executeCalculation(
  config: CalculatorConfig,
  inputs: Record<string, any>,
  countryCode: string
): Record<string, any> {
  const engineFunc = engineMap[config.formula];
  if (!engineFunc) {
    return {};
  }

  const rules = getCountryRules(countryCode);
  let calculated: any = {};

  try {
    switch (config.formula) {
      case "calculateIncomeTax":
        const taxResult = engineFunc(inputs.grossIncome, countryCode, {
          filingStatus: inputs.filingStatus || "single",
          taxRegime: inputs.taxRegime || "new",
          calculateHecs: config.id === "hecs-help",
          thirtyPercentExpats: config.id === "thirty-percent-ruling",
          churchTax: inputs.churchTax === "yes",
        });
        calculated = {
          ...taxResult,
          takeHome: taxResult.takeHomePay,
          effectiveRate: taxResult.effectiveTaxRate,
          marginalRate: taxResult.marginalTaxRate,
        };
        break;

      case "calculateNoTaxOnTips":
        calculated = engineFunc(inputs.tipIncome ?? 0, inputs.filingStatus || "single", inputs.magi ?? 0);
        break;

      case "calculateNoTaxOnOvertime":
        calculated = engineFunc(inputs.overtimeIncome ?? 0, inputs.filingStatus || "single", inputs.magi ?? 0);
        break;

      case "calculateInstantTaxDeduction":
        calculated = engineFunc(
          inputs.grossIncome ?? 0,
          inputs.actualExpenses ?? 300,
          inputs.hasReceipts === "yes"
        );
        break;

      case "calculateSingaporeSavingsBond":
        calculated = engineFunc(
          inputs.principal ?? 10000,
          inputs.yearsToHold ?? 10
        );
        break;

      case "calculateSeniorDeduction":
        calculated = engineFunc(
          inputs.age ?? 0,
          inputs.filingStatus || "single",
          inputs.magi ?? 0,
          inputs.spouseAge ?? undefined
        );
        break;

      case "calculateAutoLoanInterestDeduction":
        calculated = engineFunc(
          inputs.autoInterestPaid ?? 0,
          inputs.magi ?? 0,
          inputs.autoUsAssembled || "yes",
          inputs.filingStatus || "single"
        );
        break;

      case "calculateObbbaTotalSavings":
        calculated = engineFunc(
          inputs.filingStatus || "single",
          inputs.magi ?? 0,
          inputs.tipIncome ?? 0,
          inputs.overtimeIncome ?? 0,
          inputs.age ?? 0,
          inputs.autoInterestPaid ?? 0,
          inputs.autoUsAssembled || "yes",
          inputs.spouseAge ?? undefined
        );
        break;

      case "calculatePaydaySuperImpact":
        calculated = engineFunc(
          inputs.salary ?? 0,
          inputs.currentBalance ?? 0,
          inputs.years ?? 0,
          inputs.returnRate ?? 7.0,
          inputs.frequency || "fortnightly"
        );
        break;

      case "calculatePaydaySuperCompliance":
        calculated = engineFunc(
          inputs.employees ?? 1,
          inputs.avgSalary ?? 0,
          inputs.payCycle || "fortnightly",
          inputs.businessInterestRate ?? 4.0
        );
        break;

      case "calculateCashIsa2027CapImpact":
        calculated = engineFunc(
          inputs.currentSavings ?? 0,
          inputs.monthlyContribution ?? 0,
          inputs.interestRate ?? 4.5,
          inputs.age ?? 35,
          inputs.taxRate ?? 22
        );
        break;

      case "calculateBox3WealthTax":
        calculated = engineFunc(
          inputs.savings ?? 0,
          inputs.investments ?? 0,
          inputs.debts ?? 0,
          inputs.hasPartner === "yes",
          inputs.actualSavingsReturn ?? 0,
          inputs.actualInvestmentReturn ?? 0,
          inputs.actualDebtInterest ?? 0
        );
        break;

      case "calculateAktivrenteTaxSavings":
        calculated = engineFunc(
          inputs.pensionIncome ?? 0,
          inputs.monthlyActiveEarnings ?? 0,
          inputs.churchTax === "yes"
        );
        break;

      case "calculateFruehstartRenteGrowth":
        calculated = engineFunc(
          inputs.childAge ?? 6,
          inputs.assumedReturn ?? 6.0
        );
        break;

      case "calculateIrsMileageRateSplit":
        calculated = engineFunc(
          inputs.businessMilesH1 ?? 5000,
          inputs.businessMilesH2 ?? 5000,
          inputs.medicalMilesH1 ?? 0,
          inputs.medicalMilesH2 ?? 0,
          inputs.charityMiles ?? 0,
          inputs.marginalTaxRate ?? 24,
          inputs.usedActualExpensesH1 || "no",
          inputs.medicalRateH2 ?? 0.235
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "H1 Business Deduction (72.5¢/mi)", amount: calculated.h1BusinessDeduction },
            { label: "H2 Business Deduction (76.0¢/mi)", amount: calculated.h2BusinessDeduction },
            { label: "Medical & Moving Deduction", amount: calculated.medicalMovingDeduction },
            { label: "Charitable Deduction (14.0¢/mi)", amount: calculated.charityDeduction },
            { label: "Total Mileage Tax Deduction", amount: calculated.totalMileageDeduction },
            { label: "Estimated Income Tax Savings", amount: calculated.estimatedTaxSavings }
          ];
        }
        break;

      case "calculate13thAhvPension":
        calculated = engineFunc(
          inputs.maritalStatus || "single",
          inputs.contributionYears ?? 44,
          inputs.monthlyAhvPension ?? 2520,
          inputs.averageAnnualSalary ?? 85000
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Monthly AHV Pension Rate", amount: calculated.monthlyAhvPension },
            { label: "Regular 12-Month Annual Pension", amount: calculated.regularAnnualPension },
            { label: "December 13th Bonus Supplement", amount: calculated.december13thBonus },
            { label: "Total Annual AHV Payout (13 Months)", amount: calculated.totalAnnualAHV }
          ];
        }
        break;

      case "calculateStudentLoanSaveTransition":
        calculated = engineFunc(
          inputs.agi ?? 65000,
          inputs.familySize ?? 1,
          inputs.loanBalance ?? 40000,
          inputs.interestRate ?? 6.0,
          inputs.loanType || "direct_sub_unsub",
          inputs.isPublicService || "no"
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Terminated SAVE Plan Payment (~5%)", amount: calculated.oldSaveMonthlyPayment },
            { label: "New Repayment Assistance Plan (RAP)", amount: calculated.rapMonthlyPayment },
            { label: "Legacy IBR Payment (10% IDR)", amount: calculated.newIdrMonthlyPayment },
            { label: "Standard 10-Year Amortized Payment", amount: calculated.standard10YrMonthlyPayment },
            { label: "Monthly Payment Shock Increase", amount: calculated.paymentShockDollar }
          ];
        }
        break;

      case "calculateHraExemption8Cities":
        calculated = engineFunc(
          inputs.basicSalaryAnnual ?? 600000,
          inputs.daAnnual ?? 0,
          inputs.hraReceivedAnnual ?? 240000,
          inputs.rentPaidAnnual ?? 300000,
          inputs.cityCategory || "metro8",
          inputs.taxRegime || "old",
          inputs.marginalTaxRate
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Actual HRA Received", amount: calculated.actualHraAnnual },
            { label: "Tax-Exempt HRA Amount", amount: calculated.exemptHraAnnual },
            { label: "Taxable HRA Portion", amount: calculated.taxableHraAnnual },
            { label: "Estimated Annual Tax Savings", amount: calculated.annualTaxSavings }
          ];
        }
        break;

      case "calculate401kContributionMaximizer":
        calculated = engineFunc(
          inputs.annualSalary ?? 120000,
          inputs.age ?? 45,
          inputs.paycheckFrequency || "biweekly",
          inputs.ytdContributions ?? 0,
          inputs.remainingPaychecks ?? 12,
          inputs.employerMatchPercent ?? 50,
          inputs.employerMatchCapPercent ?? 6,
          inputs.marginalTaxRate ?? 24,
          inputs.priorYearW2Income ?? 120000
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Base Employee Deferral Limit", amount: calculated.baseLimit },
            { label: "Age 50+ / Super Catch-Up Limit", amount: calculated.catchUpLimit },
            { label: "Total Employee Contribution", amount: calculated.totalEmployeeLimit },
            { label: "Remaining Deferral Room", amount: calculated.remainingEmployeeLimit },
            { label: "Employer Match Amount", amount: calculated.employerMatchDollars },
            { label: "Total Combined 401(k) Funding", amount: calculated.totalCombinedContributions },
            { label: "Estimated Income Tax Savings", amount: calculated.estimatedTaxSavings }
          ];
        }
        break;

      case "calculateCpfRetirementAgeTransition":
        calculated = engineFunc(
          inputs.currentAge ?? 60,
          inputs.raBalance ?? 200000,
          inputs.oaBalance ?? 50000,
          inputs.targetPayoutAge ?? 65
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "RA Base Annual Interest (4.0%)", amount: calculated.annualBaseInterestRA },
            { label: "Extra Tier 1 & Tier 2 Interest Bonus", amount: calculated.extraInterestBonus },
            { label: "Total Annual Interest Earned", amount: calculated.totalAnnualInterestEarned },
            { label: "1-Year Compounding Bonus (Age Shift)", amount: calculated.oneYearCompoundingBonus },
            { label: "Projected RA Balance after 1 Year", amount: calculated.projectedRaBalanceIn1Year }
          ];
        }
        break;

      case "calculateStage3TaxCut15PercentBracket":
        calculated = engineFunc(
          inputs.taxableSalary ?? 75000,
          inputs.soleTraderNetIncome ?? 25000,
          inputs.eligibleAssetPurchases ?? 12000,
          inputs.businessUsePercent ?? 100
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Total Combined Gross Income", amount: calculated.totalGrossIncome },
            { label: "$20,000 Instant Asset Write-Off Claimed", amount: calculated.cappedWriteOffDeduction },
            { label: "Net Taxable Income after Deductions", amount: calculated.netTaxableIncome },
            { label: "Previous FY 2025-26 Tax Liability", amount: calculated.oldTotalTax },
            { label: "New FY 2026-27 Tax Liability", amount: calculated.newTotalTax },
            { label: "Tax Cut Savings from 15% Bracket", amount: calculated.taxCutSavings15Percent },
            { label: "Tax Savings from $20k Write-Off", amount: calculated.writeOffTaxSavings },
            { label: "Total Annual Tax Savings", amount: calculated.totalCombinedSavings }
          ];
        }
        break;

      case "calculateRbiMisSellingCompensation":
        calculated = engineFunc(
          Number(inputs.loanAmount ?? 2500000),
          Number(inputs.forcedInsurancePremium ?? 150000),
          Number(inputs.hiddenProcessingFees ?? 25000),
          Number(inputs.monthsElapsed ?? 12),
          inputs.saleDatePeriod || "post_july_2026"
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Mis-sold Amount (Premium + Hidden Fees)", amount: calculated.baseMisSoldAmount },
            { label: "Accrued Interest Penalty (9% p.a.)", amount: calculated.accruedInterestPenalty },
            { label: "Total Ombudsman Claim", amount: calculated.totalClaimAmount }
          ];
        }
        break;

      case "calculateOsfiMortgageSwitchExemption":
        calculated = engineFunc(
          inputs.mortgageBalance ?? 450000,
          inputs.propertyValue ?? 650000,
          inputs.contractRate ?? 4.85,
          inputs.additionalCashOut ?? 0,
          inputs.isAmortizationExtended || "no",
          inputs.annualHouseholdIncome ?? 110000
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Total Mortgage Amount", amount: calculated.totalMortgageAmount },
            { label: "Actual Monthly Payment (Contract Rate)", amount: calculated.actualMonthlyPayment },
            { label: "Qualifying Monthly Payment (Stress Test Rate)", amount: calculated.stressTestMonthlyPayment },
            { label: "Loan-to-Value (LTV) Ratio (%)", amount: calculated.ltvRatio },
            { label: "Loan-to-Income (LTI) Ratio (x)", amount: calculated.ltiRatio }
          ];
        }
        break;

      case "calculateIrsAfrIntraFamilyLoan":
        calculated = engineFunc(
          inputs.loanAmount ?? 100000,
          inputs.loanTermYears ?? 5,
          inputs.interestRateCharged ?? 3.0,
          inputs.compoundingFrequency || "annual",
          inputs.loanAmount ?? 100000
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Annual Interest Charged at Family Rate", amount: calculated.annualInterestCharged },
            { label: "Annual Required Interest at IRS AFR Rate", amount: calculated.annualRequiredAfrInterest },
            { label: "Annual Imputed Interest / Gift Amount", amount: calculated.annualImputedInterest },
            { label: "Total Imputed Interest over Loan Term", amount: calculated.totalImputedInterestTerm }
          ];
        }
        break;

      case "calculateFairWorkMinimumWageIncrease":
        calculated = engineFunc(
          inputs.employmentType || "award_fulltime",
          inputs.currentHourlyRate ?? 30.00,
          inputs.weeklyHours ?? 38
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "New Base Hourly Rate", amount: calculated.newBaseHourlyRate },
            { label: "Effective Hourly Rate (incl. Casual Loading)", amount: calculated.effectiveHourlyRate },
            { label: "Hourly Pay Increase Amount", amount: calculated.hourlyIncreaseDollars },
            { label: "New Gross Weekly Earnings", amount: calculated.newWeeklyEarnings },
            { label: "Weekly Pay Increase Amount", amount: calculated.weeklyIncreaseDollars },
            { label: "Annual Gross Income Increase", amount: calculated.annualGrossIncrease },
            { label: "Annual Employer Super Boost (12%)", amount: calculated.annualSuperBoost }
          ];
        }
        break;

      case "calculateNzFmaCccfaEligibility":
        calculated = engineFunc(
          inputs.loanOriginationDate || "post_july_2026",
          inputs.lenderType || "registered_bank",
          inputs.issueType || "predatory_irresponsible_lending",
          inputs.interestPaid ?? 4000,
          inputs.feesPaid ?? 500,
          inputs.hasFmaLicense || "yes"
        );
        break;



      case "calculateAltersvorsorgedepotGrowth":
        calculated = engineFunc(
          inputs.annualContribution ?? 1800,
          inputs.numberOfChildren ?? 1,
          inputs.isUnder25 || "no",
          inputs.investmentTermYears ?? 30,
          inputs.expectedEtfReturn ?? 7.0
        );
        if (calculated && !calculated.breakdown) {
          calculated.breakdown = [
            { label: "Annual Grundförderung (Base Subsidy)", amount: calculated.annualBaseSubsidy },
            { label: "Annual Kinderzulage (Child Subsidy)", amount: calculated.annualChildSubsidy },
            { label: "Total Annual State Subsidy", amount: calculated.totalAnnualSubsidy },
            { label: "Total Investor Contributions (Term)", amount: calculated.totalInvestorContributions },
            { label: "Total State Subsidies Earned (Term)", amount: calculated.totalStateSubsidies },
            { label: "Projected ETF Portfolio Value", amount: calculated.projectedPortfolioValue }
          ];
        }
        break;

      case "calculateTrumpAccountGrowth":
        calculated = engineFunc(
          inputs.childBirthYear ?? 2026,
          inputs.includeFederalSeed || "yes",
          inputs.employerContribution ?? 0,
          inputs.annualContribution ?? 2000,
          inputs.annualReturn ?? 7.5,
          inputs.retirementAge ?? 65,
          inputs.currentAge ?? 0
        );
        break;

      case "calculateCompulsoryKiwiSaverImpact":
        calculated = engineFunc(
          inputs.grossSalary ?? 75000,
          inputs.currentVoluntaryRate ?? 3.5,
          inputs.proposedScenario || "gradual-8",
          inputs.remunerationType || "added",
          inputs.annualReturn ?? 6.0,
          inputs.termYears ?? 30,
          inputs.currentBalance ?? 15000,
          inputs.currentAge ?? 35
        );
        break;

      case "calculateCanadaNewHomeGstRebate":
        calculated = engineFunc(
          inputs.homePrice ?? 850000,
          inputs.isFirstTimeBuyer || "yes",
          inputs.hbpAmount ?? 60000,
          inputs.marginalTaxRate ?? 30,
          inputs.province || "other"
        );
        break;

      case "calculateIrsPenaltyReliefAep":
        calculated = engineFunc(
          inputs.unpaidTax ?? 5000,
          inputs.monthsLate ?? 3,
          inputs.penaltyType || "both",
          inputs.hasCleanHistory || "yes",
          inputs.depositDaysLate ?? 10
        );
        break;

      case "calculateUkBnplDpcRights":
        calculated = engineFunc(
          inputs.purchaseAmount ?? 500,
          inputs.repaymentTermMonths ?? 3,
          inputs.missedPayments ?? 1,
          inputs.provider || "klarna",
          inputs.purchaseDate || "post"
        );
        break;

      case "calculateVaPartialClaim":
        calculated = engineFunc(
          inputs.unpaidPrincipal ?? 300000,
          inputs.arrearageAmount ?? 15000,
          inputs.hasCovidArrears || "no",
          inputs.trialPlanCompleted || "yes"
        );
        break;

      case "calculateLrsTcs":
        calculated = engineFunc(
          inputs.remittanceAmount ?? 500000,
          inputs.purpose || "education",
          inputs.purpose === "education_loan" ? "yes" : "no",
          inputs.cumulativeFYRemittances ?? 0
        );
        break;

      case "calculateCibilUpdatePredictor":
        calculated = engineFunc(
          inputs.paymentDate ?? 1,
          inputs.paymentMonth ?? 7
        );
        break;

      case "calculateKvdrEligibility":
        calculated = engineFunc(
          inputs.workingStartAge ?? 20,
          inputs.retirementAge ?? 67,
          inputs.gkvYearsSecondHalf ?? 15,
          inputs.childrenCount ?? 0
        );
        break;

      case "calculateWhmTaxTakeHome":
        calculated = engineFunc(
          inputs.wageType || "hourly",
          inputs.wageAmount ?? 30,
          inputs.hoursPerWeek ?? 38,
          inputs.employerRegistered || "registered"
        );
        break;

      case "calculateOverseasStudentLoanRepayment":
        calculated = engineFunc(
          inputs.loanBalance ?? 25000,
          inputs.annualExtraRepayments ?? 1000
        );
        break;

      case "calculateNzFeesFreeCutoff":
        calculated = engineFunc(
          inputs.completionTiming || "by_2026_12_31",
          inputs.qualifyingFees ?? 8600,
          inputs.otherEligibilityMet || "yes",
          inputs.previousFeesFree || "no"
        );
        break;

      case "calculateAuOverseasPensionSupplement":
        calculated = engineFunc(
          inputs.fortnightlySupplement ?? 56,
          inputs.daysOverseas ?? 98,
          inputs.mainPaymentPayableDays ?? 98,
          inputs.travelType || "temporary"
        );
        break;

      case "calculateCaliforniaWildfireForbearance":
        calculated = engineFunc(
          inputs.priorForbearanceMonths ?? 10,
          inputs.requestedAdditionalMonths ?? 12,
          inputs.monthlyMortgagePayment ?? 2500
        );
        break;

      case "calculateZzpTaxDeductionTakeHome":
        calculated = engineFunc(
          inputs.grossRevenues ?? 60000,
          inputs.businessExpenses ?? 10000,
          inputs.meetUrencriterium === "yes",
          inputs.isStarter === "yes"
        );
        break;

      case "calculateNewLabourCodesCtcRestructuring":
        calculated = engineFunc(
          inputs.includedWagesMonthly ?? 40000,
          inputs.excludedCashMonthly ?? 50000,
          inputs.employerPfMonthly ?? 3000,
          inputs.statutoryBonusMonthly ?? 0,
          inputs.pfCoverage || "yes",
          inputs.pfRate ?? 12,
          inputs.pfContributionMode || "statutory_cap"
        );
        break;

      case "calculateBudget2026CpfTopUp":
        calculated = engineFunc(
          inputs.birthYear ?? 1970,
          inputs.cpfRetirementSavings ?? 50000,
          inputs.propertyAnnualValue ?? 15000,
          inputs.numberOfProperties ?? 1
        );
        break;

      case "calculateUpsPension":
        calculated = engineFunc(
          inputs.yearsOfService ?? 25,
          inputs.lastBasicPay ?? 50000,
          inputs.daRate ?? 50
        );
        break;

      case "calculatePillar3aRetroactiveBuyIn":
        calculated = engineFunc(
          inputs.employmentType ?? "salaried",
          inputs.taxableIncome ?? 100000,
          inputs.gapYears ?? 5,
          inputs.marginalTaxRate ?? 25,
          inputs.expectedReturn ?? 6.0
        );
        break;

      case "calculateCapitalGains":
        calculated = engineFunc(inputs.gain, inputs.holdingPeriod, countryCode);
        break;

      case "calculateSelfEmploymentTax":
        calculated = engineFunc(inputs.income, countryCode);
        break;

      case "calculateMortgage":
        calculated = engineFunc(inputs.principal, inputs.annualRate, inputs.termYears);
        break;

      case "calculateAffordability":
        calculated = engineFunc(
          inputs.annualIncome,
          inputs.monthlyDebts,
          inputs.annualRate,
          inputs.termYears,
          inputs.downPayment
        );
        break;

      case "calculateOverpayment":
        calculated = engineFunc(
          inputs.principal,
          inputs.annualRate,
          inputs.termYears,
          inputs.extraMonthlyPayment
        );
        break;

      case "calculateRefinance":
        calculated = engineFunc(
          inputs.principal,
          inputs.annualRate,
          inputs.termYears,
          inputs.newRate,
          inputs.newTermYears ?? inputs.termYears,
          inputs.closingCosts
        );
        break;

      case "calculateRentVsBuy":
        calculated = engineFunc(
          inputs.homePrice,
          inputs.downPayment,
          inputs.annualRate,
          inputs.termYears,
          inputs.monthlyRent,
          inputs.yearsPlanned,
          inputs.appreciationRate,
          inputs.maintenancePercent
        );
        break;

      case "calculateAutoLoan":
        const autoPrincipal = Math.max(0, inputs.principal - (inputs.downPayment || 0) - (inputs.tradeIn || 0));
        calculated = engineFunc(autoPrincipal, inputs.annualRate, inputs.termYears);
        break;

      case "compoundInterest":
        calculated = engineFunc(
          inputs.principal ?? 0,
          inputs.annualRate,
          inputs.termYears,
          12,
          inputs.monthlyContribution ?? 0
        );
        break;



      case "retirementPlanner":
        calculated = engineFunc(
          inputs.currentAge,
          inputs.retirementAge,
          inputs.lifeExpectancy,
          inputs.currentSavings,
          inputs.monthlySavings,
          inputs.annualReturnPre,
          inputs.annualReturnPost,
          inputs.inflationRate,
          inputs.monthlyIncomeGoal
        );
        break;

      case "fireCalculator":
        calculated = engineFunc(
          inputs.annualExpenses,
          inputs.currentSavings,
          inputs.savingsRate,
          inputs.investmentReturn,
          inputs.inflationRate,
          inputs.annualIncome
        );
        break;

      case "corpusNeeded":
        const corpusRaw = engineFunc(
          inputs.monthlyExpense,
          inputs.termYears,
          inputs.annualRate,
          inputs.inflationRate
        );
        calculated = { corpus: corpusRaw };
        break;

      case "swr":
        calculated = engineFunc(
          inputs.principal,
          inputs.termYears,
          inputs.inflationRate,
          inputs.swr,
          inputs.annualRate ?? rules.investments.historicalMarketReturn * 100
        );
        break;

      case "pensionEstimator":
        const pensionRaw = engineFunc(inputs.termYears, inputs.principal, countryCode);
        calculated = typeof pensionRaw === "number" ? { annualPension: pensionRaw } : pensionRaw;
        break;



      case "dividendGrowth":
        const reinvest = inputs.reinvest === "yes";
        const sharePrice = inputs.principal > 0 ? inputs.principal / 100 : 100;
        const initialShares = inputs.principal > 0 ? inputs.principal / sharePrice : 100;
        calculated = engineFunc(
          initialShares,
          sharePrice,
          inputs.dividendYield,
          inputs.dividendGrowth,
          inputs.priceGrowth,
          inputs.termYears,
          reinvest
        );
        break;

      case "dollarCostAverage":
        const dcaYears = inputs.termYears ?? 5;
        const dcaRate = inputs.annualRate ?? 8;
        const totalMonths = dcaYears * 12;
        const monthlyRate = dcaRate / 100 / 12;
        const startPrice = 100;
        const dcaPrices = Array.from({ length: totalMonths }, (_, i) => {
          const t = i + 1;
          const trend = startPrice * Math.pow(1 + monthlyRate, t);
          const volatility = 1 + 0.08 * Math.sin(t * 0.5);
          return trend * volatility;
        });
        calculated = engineFunc(inputs.monthlyContribution, dcaPrices);
        break;

      case "calculateDebtPayoff":
        const debts = [];
        if (inputs.debt1Balance > 0) {
          debts.push({ name: "Debt 1", balance: inputs.debt1Balance, rate: inputs.debt1Rate, minPayment: inputs.debt1Min });
        }
        if (inputs.debt2Balance > 0) {
          debts.push({ name: "Debt 2", balance: inputs.debt2Balance, rate: inputs.debt2Rate, minPayment: inputs.debt2Min });
        }
        if (inputs.debt3Balance > 0) {
          debts.push({ name: "Debt 3", balance: inputs.debt3Balance, rate: inputs.debt3Rate, minPayment: inputs.debt3Min });
        }
        const strategy = inputs.strategy || "snowball";
        calculated = engineFunc(debts, strategy, inputs.extraBudget);
        break;

      case "emergencyFund":
        calculated = engineFunc(inputs.monthlyExpenses, inputs.termYears);
        break;

      case "savingsGoal":
        calculated = engineFunc(
          inputs.targetAmount,
          inputs.currentSavings,
          inputs.annualRate,
          inputs.termYears
        );
        break;

      case "inflationImpact":
        calculated = engineFunc(inputs.currentAmount, inputs.annualInflation, inputs.termYears);
        break;

      case "realReturn":
        const realReturnRaw = engineFunc(inputs.nominalRate, inputs.annualInflation);
        calculated = typeof realReturnRaw === "number" ? { realRate: realReturnRaw } : realReturnRaw;
        break;

      case "fixedDeposit":
        calculated = engineFunc(inputs.principal, inputs.annualRate, inputs.termYears);
        break;

      case "budget503020":
        calculated = engineFunc(inputs.monthlyNetIncome);
        break;

      case "debtToIncome":
        const dtiRaw = engineFunc(inputs.monthlyDebts, inputs.monthlyGrossIncome);
        calculated = typeof dtiRaw === "number" ? { ratio: dtiRaw } : dtiRaw;
        break;

      case "netWorth":
        const netWorthRaw = engineFunc(inputs.assets, inputs.liabilities);
        calculated = typeof netWorthRaw === "number" ? { netWorth: netWorthRaw } : netWorthRaw;
        break;



      case "calculatePremiumBondsExpectedReturn":
        calculated = engineFunc(inputs.investmentAmount, inputs.months, inputs.prizeRate, inputs.inflationRate);
        break;

      case "calculateApraDtiCapCheck":
        calculated = engineFunc(
          inputs.annualIncome ?? 0,
          inputs.currentTotalDebt ?? 0,
          inputs.newLoanAmount ?? 0,
          inputs.existingMonthlyDebtPayments ?? 0
        );
        break;

      case "calculateOffsetErrorCost":
        calculated = engineFunc(
          inputs.loanBalance ?? 0,
          inputs.offsetBalance ?? 0,
          inputs.interestRate ?? 0,
          inputs.monthsUnlinked ?? 0
        );
        break;

      case "calculateCapitalOne360SettlementEstimate":
        calculated = engineFunc(
          inputs.avgBalance ?? 0,
          inputs.monthsHeld ?? 0,
          inputs.avgRateGap ?? 0,
          inputs.stillHasAccount || "yes"
        );
        break;

      case "calculateDownPayment":
        calculated = engineFunc(
          inputs.targetPrice,
          inputs.downPercent,
          inputs.currentSavings,
          inputs.monthlySave,
          inputs.annualRate
        );
        break;

      case "calculateReturns":
        const simpleReturns = engineFunc(inputs.principal, inputs.endBalance);
        const beginValue = inputs.principal ?? 0;
        const endValue = inputs.endBalance ?? 0;
        const years = inputs.termYears ?? 1;
        let cagrRate = 0;
        if (beginValue > 0 && endValue > 0 && years > 0) {
          cagrRate = (Math.pow(endValue / beginValue, 1 / years) - 1) * 100;
        }
        calculated = {
          ...simpleReturns,
          cagr: cagrRate,
        };
        break;

      default:
        console.warn(`Unknown formula: ${config.formula}`);
    }
  } catch (err) {
    console.error("Calculation Error:", err);
  }

  return calculated;
}
